import React from "react";
import {
  createBrowserRouter,
  matchRoutes as reactRouterMatchRoutes,
} from "react-router";
import Homepage, { loader as homepageLoader } from "./page/Homepage";
import Calendar, { loader as calendarLoader } from "./page/Calendar";
import Products, { loader as companyLoader } from "./page/Products.jsx";
import Dashboard, { loader as dashboardLoader } from "./page/Dashboard.jsx";
import Employees, { loader as employeesLoader } from "./page/Employees.jsx";
import Client, { loader as clientLoader } from "./page/Client";
import Clients, { loader as clientsLoader } from "./page/Clients";
import History, { loader as historyLoader } from "./page/History";
import ServiceEdit, {
  loader as serviceEditLoader,
} from "./page/ServiceEditPage";
import Reception, { loader as receptionLoader } from "./page/Reception";
import SeatsLive, { loader as seatsLiveLoader } from "./page/SeatsLive";
import Locations, { loader as locationsLoader } from "./page/Locations";
import Product, { loader as productLoader } from "./page/Product";
import RFIDAuth, { loader as rfidAuthLoader } from "./page/RFIDAuth";
import Algorithm, {
  loader as reservationSettingsLoader,
} from "./page/Algorithm.jsx";
import AlgorithmEdit, {
  loader as reservationSettingsEditLoader,
} from "./page/AlgorithmEdit.jsx";
import LocationEdit, {
  loader as locationEditLoader,
} from "./page/LocationEdit";
import Resources, { loader as calendarsLoader } from "./page/Resources.jsx";
import ResourceEdit, {
  loader as resourceEditLoader,
} from "./page/ResourceEdit.jsx";
import ErrorElement from "./components/ErrorElement";
import Layout from "./components/Layout";
import LoginPage from "./page/Login";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import * as auth from "./auth";
import { currentLocationFromRequest } from "./components/LocationSelector";
import ClientAppointment, {
  loader as clientAppointmentLoader,
} from "./page/ClientAppointment";
import LoginLayout from "./components/LoginLayout.jsx";
import WebhookConfig, {
  loader as webhookConfigLoader,
} from "./page/WebhookConfig.jsx";
import Cleaning, { loader as cleaningLoader } from "./page/Cleaning";
import Settings, { loader as settingsLoader } from "./page/Settings";

const error404 = {
  data: "",
  internal: false,
  status: 404,
  statusText: "Not Found",
};

async function dataStrategy({ matches, request }) {
  const matchesToLoad = matches.filter((m) => m.shouldLoad);

  if (!matchesToLoad.every((match) => match.route?.handle?.anonymous)) {
    auth.ensureAuthenticated();
  }

  const authToken = auth.tokenPayload(auth.getAuthenticationToken());
  const currentLocation = currentLocationFromRequest(request);
  const currentRoles = new Set(authToken?.roles?.[currentLocation] ?? []);
  if (
    !matchesToLoad.every((match) =>
      isGrantedAny(currentRoles, match.route?.handle?.anyRole),
    )
  ) {
    throw error404;
  }

  const results = await Promise.all(matchesToLoad.map((m) => m.resolve()));
  return results.reduce(
    (acc, result, i) =>
      Object.assign(acc, { [matchesToLoad[i].route.id]: result }),
    {},
  );
}

/** @type {import('react-router').RouteObject} */
export const routes = [
  {
    path: "/",
    element: (
      <Layout>
        <Homepage />
      </Layout>
    ),
    loader: homepageLoader,
    errorElement: <ErrorElement />,
  },
  {
    path: "/calendar",
    element: (
      <Layout>
        <Calendar />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: calendarLoader,
    handle: {
      anyRole: new Set([
        role.Back_Office,
        role.Front_Office,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/products",
    element: (
      <Layout>
        <Products />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: companyLoader,
    handle: {
      anyRole: new Set([
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/dashboard",
    element: (
      <Layout>
        <Dashboard />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: dashboardLoader,
    handle: {
      anyRole: new Set([role.Floor_Manager, role.Manager_Clinic]),
    },
  },
  {
    path: "/employees",
    element: (
      <Layout>
        <Employees />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: employeesLoader,
    handle: {
      anyRole: new Set([
        role.Floor_Manager,
        role.Manager_Clinic,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/history",
    element: (
      <Layout>
        <History />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: historyLoader,
    handle: {
      anyRole: new Set([
        role.Manager_Customer_Care,
        role.Manager_Clinic,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/settings",
    element: (
      <Layout>
        <Settings />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: settingsLoader,
    handle: {
      anyRole: new Set([
        role.Floor_Manager,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
      ]),
    },
  },
  {
    path: "/resources",
    element: (
      <Layout>
        <Resources />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: calendarsLoader,
    handle: {
      anyRole: new Set([
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/resources/:id/edit",
    element: (
      <Layout>
        <ResourceEdit />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: resourceEditLoader,
    handle: {
      anyRole: new Set([role.Manager_Clinic, role.Manager_Customer_Care]),
    },
  },
  {
    path: "/clients",
    element: (
      <Layout>
        <Clients />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: clientsLoader,
    handle: {
      anyRole: new Set([
        role.Floor_Manager,
        role.Treatment_Coordinator,
        role.Doctor,
        role.Back_Office,
        role.Front_Office,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/clients/:id",
    element: (
      <Layout>
        <Client />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: clientLoader,
    handle: {
      anyRole: new Set([
        role.Floor_Manager,
        role.Treatment_Coordinator,
        role.Doctor,
        role.Back_Office,
        role.Front_Office,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/clients/:id/:appointmentId",
    element: (
      <Layout>
        <ClientAppointment />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: clientAppointmentLoader,
    handle: {
      anyRole: new Set([
        role.Floor_Manager,
        role.Treatment_Coordinator,
        role.Doctor,
        role.Back_Office,
        role.Front_Office,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only,
        role.Read_Only_Full,
        role.Dental_Assistant, //to allow nurse to see client appointments
      ]),
    },
  },
  {
    path: "/products/:id",
    element: (
      <Layout>
        <Product />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: productLoader,
    handle: {
      anyRole: new Set([role.Manager_Clinic, role.Manager_Customer_Care]),
    },
  },
  {
    path: "/reception",
    element: (
      <Layout>
        <Reception />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: receptionLoader,
    handle: {
      anyRole: new Set([role.Front_Office, role.Manager_Customer_Care]),
    },
  },
  {
    path: "/seats-live",
    element: (
      <Layout>
        <SeatsLive />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: seatsLiveLoader,
    handle: {
      anyRole: new Set([
        role.Front_Office,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/algorithm",
    element: (
      <Layout>
        <Algorithm />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: reservationSettingsLoader,
    handle: {
      anyRole: new Set([
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/algorithm/edit",
    element: (
      <Layout>
        <AlgorithmEdit />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: reservationSettingsEditLoader,
    handle: {
      anyRole: new Set([role.Manager_Clinic, role.Manager_Customer_Care]),
    },
  },
  {
    path: "/products/services/:id/edit",
    element: (
      <Layout>
        <ServiceEdit />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: serviceEditLoader,
    handle: {
      anyRole: new Set([role.Manager_Clinic, role.Manager_Customer_Care]),
    },
  },
  {
    path: "/locations",
    element: (
      <Layout>
        <Locations />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: locationsLoader,
    handle: {
      anyRole: new Set([
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Floor_Manager,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/locations/:id/edit",
    element: (
      <Layout>
        <LocationEdit />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: locationEditLoader,
    handle: {
      anyRole: new Set([
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Floor_Manager,
      ]),
    },
  },
  {
    path: "/webhooks",
    element: (
      <Layout>
        <WebhookConfig />
      </Layout>
    ),
    errorElement: <ErrorElement />,
    loader: webhookConfigLoader,
    handle: {
      anyRole: new Set([
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only_Full,
      ]),
    },
  },
  {
    path: "/login",
    element: (
      <LoginLayout>
        <LoginPage />
      </LoginLayout>
    ),
    errorElement: <ErrorElement />,
  },
  {
    path: "/rfid-auth",
    element: (
      <LoginLayout>
        <RFIDAuth />
      </LoginLayout>
    ),
    errorElement: <ErrorElement />,
    loader: rfidAuthLoader,
    handle: {
      anonymous: true,
    },
  },
  {
    path: "/cleaning/:id",
    element: (
      <LoginLayout>
        <Cleaning />
      </LoginLayout>
    ),
    errorElement: <ErrorElement />,
    loader: cleaningLoader,
  },
  {
    path: "*",
    errorElement: <ErrorElement />,
    loader: function () {
      throw new Response("Not Found", { status: 404, statusText: "Not Found" });
    },
  },
];

export function matchRoutes(path) {
  return reactRouterMatchRoutes(routes, path);
}

export const anyRoleByPath = routes.reduce((acc, route) => {
  const anyRole = route.handle?.anyRole;
  if (anyRole != null) {
    acc[route.path] = anyRole;
  }

  return acc;
}, {});

export const router = createBrowserRouter(routes, {
  dataStrategy: dataStrategy,
  future: {
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});
